import { useQueryClient } from "@tanstack/react-query";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";

import { fetchLoginId } from "hooks/useLoginId";
import { fetchOrganization } from "hooks/useOrganization";
import { MyAccount } from "services/account/account.types";
import { getAccounts, getMyAccounts } from "services/account/getAccounts";
import { authService } from "services/auth";
import { systemEngineEnvironment } from "services/environment/systemEngine";
import { fetchLogin } from "services/login/useLogin";
import {
  ReactQueryKeysAccount,
  ReactQueryKeysGlobal,
} from "services/queryKeys";
import { fetchRoleNames } from "services/rbac/fetchRoleNames";
import { fetchCurrentUser } from "services/users/useCurrentUser";

import { useCurrentParamsAccount } from "components/Account/useCurrentParamsAccount";

import FakePageLoader from "../App/FakePageLoader";
import { AccountContext } from "./AccountContext";
import PrivilegesLoader from "./PrivilegesLoader";

const globalDataKeys = Object.values(ReactQueryKeysGlobal);

export const AccountProvider = (props: React.PropsWithChildren) => {
  const currentAccount = useCurrentParamsAccount();
  const queryClient = useQueryClient();
  const client = useLDClient();
  const { children } = props;
  const [isInitializingAccount, setIsInitializingAccount] = useState(true);

  const initializeAccount = useCallback(
    async (nextAccount: MyAccount) => {
      if (systemEngineEnvironment.account_id === nextAccount.id) {
        setIsInitializingAccount(false);
        return null;
      }

      queryClient.removeQueries({
        predicate: query => {
          return !globalDataKeys.some(queryKey =>
            query.queryKey.includes(queryKey)
          );
        },
      });

      systemEngineEnvironment.setAccountContext(nextAccount.id);

      await systemEngineEnvironment.initializeApiEndpoint(nextAccount);

      // Org level
      queryClient.prefetchQuery({
        queryKey: [ReactQueryKeysGlobal.organization],
        queryFn: fetchOrganization,
      });
      queryClient.prefetchQuery({
        queryKey: [ReactQueryKeysGlobal.loginId],
        queryFn: fetchLoginId,
      });
      queryClient.prefetchQuery({
        queryKey: [ReactQueryKeysGlobal.myAccounts],
        queryFn: getMyAccounts,
      });
      queryClient.prefetchQuery({
        queryKey: [ReactQueryKeysGlobal.login],
        queryFn: fetchLogin,
      });

      if (nextAccount && nextAccount.accountName) {
        // Account-level
        queryClient.prefetchQuery({
          queryKey: [ReactQueryKeysAccount.rbacRoleNames],
          queryFn: () => fetchRoleNames(),
        });
        queryClient.prefetchQuery({
          queryKey: [ReactQueryKeysAccount.currentUser],
          queryFn: fetchCurrentUser,
        });
        // todo: prefetch once gcp hacks are not needed anymore
        // todo: why do we need INCLUDE_SYSTEM_ENGINE as key????
        // queryClient.prefetchQuery({
        //   queryKey: [
        //     ReactQueryKeysAccount.workspaceEngines,
        //     nextAccount.accountName,
        //     "INCLUDE_SYSTEM_ENGINE",
        //   ],
        //   queryFn: () =>
        //     fetchWorkspaceEngines({
        //       accountName: nextAccount.accountName,
        //       includeSystemEngine: true,
        //     }),
        // });
        // queryClient.prefetchQuery({
        //   queryKey: [
        //     ReactQueryKeysAccount.workspaceEngines,
        //     nextAccount.accountName,
        //   ],
        //   queryFn: () =>
        //     fetchWorkspaceEngines({
        //       accountName: nextAccount.accountName,
        //       includeSystemEngine: false,
        //     }),
        // });
        // queryClient.prefetchQuery({
        //   queryKey: [
        //     ReactQueryKeysAccount.databasesNames,
        //     nextAccount.accountName,
        //   ],
        //   queryFn: () =>
        //     fetchDatabasesNames({ accountName: nextAccount.accountName }),
        // });
      }

      const accounts = await getAccounts(nextAccount.accountName);

      const custom = {
        OrganizationName: authService.organizationName as string,
        name: authService.organizationName as string,
        OrganizationID: authService.organization?.organizationID as string,
        key: authService.organization?.organizationID as string,
      };
      if (accounts.length) {
        const [account] = accounts;
        Object.assign(custom, { created: account.created });
      }
      await client?.identify({
        kind: "multi",
        user: {
          name: nextAccount.accountName,
          key: nextAccount.id,
          Region: nextAccount.region,
        },
        organization: custom,
      });

      const auth0user = await authService.auth0client.getUser();

      // @ts-ignore
      window?.rudderanalytics?.identify({
        email: auth0user?.email,
        accountId: nextAccount.id,
        organizationName: authService.organizationName,
      });

      setIsInitializingAccount(false);
      return null;
    },
    [client, queryClient]
  );

  useEffect(() => {
    initializeAccount(currentAccount);
  }, [initializeAccount, currentAccount]);

  const getAccount = useCallback(() => {
    return currentAccount!;
  }, [currentAccount]);

  const context = useMemo(() => {
    return {
      getAccount,
    };
  }, [getAccount]);

  if (isInitializingAccount) {
    return <FakePageLoader title="Finalizing configuration..." />;
  }

  return (
    <AccountContext.Provider value={context}>
      <Suspense
        fallback={<FakePageLoader title="Finalizing configuration..." />}
      >
        <PrivilegesLoader
          key={currentAccount.accountName}
          currentAccountName={currentAccount.accountName}
        >
          {children}
        </PrivilegesLoader>
      </Suspense>
    </AccountContext.Provider>
  );
};
